import React, { useState } from 'react';
import useBreakpoint from '../hooks/useBreakpoint';
import { useTheme } from '../ThemeContext';

const COLORS = ['#0061a4', '#1b6d24', '#FF9800', '#9C27B0', '#F44336', '#00BCD4', '#FF5722', '#607D8B'];
const newId = () => Math.random().toString(36).substr(2, 9);
const fmtDol = (n) => (n < 0 ? '-' : '') + '$' + Math.abs(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export default function SavingsGoals({ goals, isAuthenticated, onGoalsChange, onSignInClick }) {
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';
  const isTablet = bp === 'tablet';
  const isDesktop = bp === 'desktop';
  const { t } = useTheme();
  const [draft, setDraft] = useState({ name: '', targetAmount: '', currentAmount: '', targetDate: '', color: COLORS[0] });

  const inputStyle = {
    border: `1px solid ${t.borderFaint}`, borderRadius: '6px', padding: '7px 10px',
    fontSize: '13px', fontFamily: 'inherit', background: t.inputBg, color: t.text, width: '150px', outline: 'none',
  };

  // Responsive sizing
  const pagePadding = isDesktop ? '20px' : isTablet ? '16px' : '10px';
  const formPadding = isDesktop ? '18px 16px' : isTablet ? '14px 12px' : '12px 10px';
  const goalsGridCols = isDesktop
    ? 'repeat(auto-fill, minmax(300px, 1fr))'
    : isTablet
    ? 'repeat(2, 1fr)'
    : '1fr';
  const summaryGridCols = isMobile ? 'repeat(2, 1fr)' : undefined;

  // ── Locked state for free users ──────────────────────────────────────────
  if (!isAuthenticated) {
    return (
      <div style={{
        minHeight: 'calc(100vh - 94px)', background: t.pageBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
      }}>
        <div style={{ textAlign: 'center', maxWidth: '460px' }}>
          <div style={{ fontSize: '36px', fontWeight: '800', letterSpacing: '-0.02em', marginBottom: '16px', color: t.text }}>Goals</div>
          <h2 style={{ margin: '0 0 10px', fontSize: '22px', fontWeight: '700', color: t.text }}>Savings Goals</h2>
          <p style={{ margin: '0 0 28px', color: t.textMuted, fontSize: '14px', lineHeight: '1.6' }}>
            Set savings targets with visual progress bars, countdown timers, and monthly contribution calculators.
            Know exactly how much to set aside each month to reach every goal on time.
          </p>

          {/* Blurred preview */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '10px', marginBottom: '28px', filter: 'blur(2px)', pointerEvents: 'none', opacity: 0.6 }}>
            {[
              { name: 'Emergency Fund', pct: 45, color: '#0061a4', saved: '$4,500', target: '$10,000' },
              { name: 'Vacation Fund', pct: 72, color: '#1b6d24', saved: '$2,160', target: '$3,000' },
              { name: 'New Car', pct: 18, color: '#FF9800', saved: '$3,600', target: '$20,000' },
              { name: 'Down Payment', pct: 31, color: '#9C27B0', saved: '$15,500', target: '$50,000' },
            ].map((g) => (
              <div key={g.name} style={{ background: t.card, borderRadius: '10px', padding: '14px', boxShadow: t.shadow, textAlign: 'left' }}>
                <div style={{ height: '4px', background: g.color, borderRadius: '2px', marginBottom: '8px' }} />
                <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '8px', color: t.text }}>{g.name}</div>
                <div style={{ height: '6px', background: t.borderLight, borderRadius: '3px', marginBottom: '6px' }}>
                  <div style={{ height: '100%', width: `${g.pct}%`, background: g.color, borderRadius: '3px' }} />
                </div>
                <div style={{ fontSize: '11px', color: t.textFaint }}>{g.saved} of {g.target}</div>
              </div>
            ))}
          </div>

          <button
            onClick={onSignInClick}
            style={{
              padding: '13px 36px', background: '#0061a4',
              color: 'white', border: 'none', borderRadius: '10px',
              fontSize: '15px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Sign In to Unlock
          </button>
          <div style={{ marginTop: '10px', fontSize: '12px', color: t.textFaintest }}>Free account required</div>
        </div>
      </div>
    );
  }

  // ── Pro: full goals UI ───────────────────────────────────────────────────
  const addGoal = () => {
    if (!draft.name || !draft.targetAmount) return;
    onGoalsChange([...goals, {
      id: newId(),
      name: draft.name,
      targetAmount: parseFloat(draft.targetAmount) || 0,
      currentAmount: parseFloat(draft.currentAmount) || 0,
      targetDate: draft.targetDate || null,
      color: draft.color,
    }]);
    setDraft({ name: '', targetAmount: '', currentAmount: '', targetDate: '', color: COLORS[goals.length % COLORS.length] });
  };

  const deleteGoal = (id) => onGoalsChange(goals.filter((g) => g.id !== id));

  const updateSaved = (id, val) =>
    onGoalsChange(goals.map((g) => g.id === id ? { ...g, currentAmount: parseFloat(val) || 0 } : g));

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const totalSaved  = goals.reduce((s, g) => s + g.currentAmount, 0);
  const totalTarget = goals.reduce((s, g) => s + g.targetAmount, 0);

  return (
    <div style={{ minHeight: 'calc(100vh - 94px)', background: t.pageBg, padding: pagePadding }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

        {/* Summary row */}
        {goals.length > 0 && (
          <div style={{
            display: isMobile ? 'grid' : 'flex',
            gridTemplateColumns: summaryGridCols,
            gap: '14px',
            flexWrap: 'wrap',
            marginBottom: '20px',
          }}>
            <SummaryCard label="Goals"        value={String(goals.length)}                        accent="#0061a4" />
            <SummaryCard label="Total Saved"  value={fmtDol(totalSaved)}                          accent="#1b6d24" />
            <SummaryCard label="Total Target" value={fmtDol(totalTarget)}                         accent="#e65100" />
            <SummaryCard label="Still Needed" value={fmtDol(Math.max(0, totalTarget - totalSaved))} accent="#ba1a1a" />
          </div>
        )}

        {/* Empty state */}
        {goals.length === 0 && (
          <div style={{ background: t.card, borderRadius: '12px', padding: '48px', textAlign: 'center', boxShadow: t.shadow, marginBottom: '20px' }}>
            <div style={{ fontSize: '32px', fontWeight: '800', color: '#bfc7d4', marginBottom: '12px' }}>No Goals</div>
            <p style={{ color: t.textFaint, fontSize: '15px', margin: 0 }}>No savings goals yet. Add your first goal below.</p>
          </div>
        )}

        {/* Goal cards */}
        {goals.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: goalsGridCols,
            gap: '16px', marginBottom: '20px',
          }}>
            {goals.map((goal) => {
              const pct = goal.targetAmount > 0
                ? Math.min(100, (goal.currentAmount / goal.targetAmount) * 100) : 0;
              const remaining  = Math.max(0, goal.targetAmount - goal.currentAmount);
              const daysLeft   = goal.targetDate
                ? Math.ceil((new Date(goal.targetDate + 'T00:00:00') - today) / 86400000) : null;
              const monthsLeft = daysLeft != null ? daysLeft / 30.44 : null;
              const perMonth   = monthsLeft != null && monthsLeft > 0 && remaining > 0
                ? remaining / monthsLeft : null;
              const reached    = pct >= 100;
              const pastDue    = daysLeft != null && daysLeft < 0 && !reached;

              return (
                <div key={goal.id} style={{ background: t.card, borderRadius: '12px', boxShadow: t.shadow, overflow: 'hidden' }}>
                  <div style={{ height: '5px', background: goal.color }} />
                  <div style={{ padding: '18px 18px 16px' }}>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: t.text }}>{goal.name}</h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {reached && <Badge text="Reached!" bg="rgba(27,109,36,0.12)" color="#1b6d24" />}
                        {pastDue  && <Badge text="Past due"  bg="rgba(186,26,26,0.12)" color="#ba1a1a" />}
                        <button onClick={() => deleteGoal(goal.id)}
                          style={{ background: 'none', border: 'none', color: t.borderFaint, cursor: 'pointer', fontSize: '18px', padding: '0 2px', lineHeight: 1, fontFamily: 'inherit' }}>×</button>
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div style={{ height: '8px', background: t.border, borderRadius: '4px', overflow: 'hidden', marginBottom: '6px' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: reached ? '#1b6d24' : goal.color, borderRadius: '4px', transition: 'width 0.4s ease' }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: t.textMuted, marginBottom: '14px' }}>
                      <span style={{ fontWeight: '600', color: t.textSub }}>{fmtDol(goal.currentAmount)} saved</span>
                      <span>{pct.toFixed(0)}% of {fmtDol(goal.targetAmount)}</span>
                    </div>

                    {/* Update saved amount */}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '14px' }}>
                      <label style={{ fontSize: '11px', color: t.textMuted, whiteSpace: 'nowrap' }}>Update saved:</label>
                      <input
                        type="number"
                        value={goal.currentAmount}
                        onChange={(e) => updateSaved(goal.id, e.target.value)}
                        style={{ flex: 1, border: `1px solid ${t.borderMid}`, borderRadius: '6px', padding: '5px 8px', fontSize: '13px', fontFamily: 'inherit', outline: 'none', minWidth: 0, background: t.inputBg, color: t.text }}
                        min="0" step="0.01"
                      />
                    </div>

                    {/* Stats grid */}
                    <div style={{ borderTop: `1px solid ${t.borderLight}`, paddingTop: '12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      <Stat label="Still needed" value={remaining > 0 ? fmtDol(remaining) : 'Done!'} color={remaining > 0 ? '#e65100' : '#1b6d24'} />
                      {daysLeft != null && (
                        <Stat
                          label={pastDue ? 'Overdue by' : 'Days left'}
                          value={pastDue ? `${Math.abs(daysLeft)}d` : `${daysLeft}d`}
                          color={pastDue ? '#ba1a1a' : '#555'}
                        />
                      )}
                      {perMonth != null && <Stat label="Need per month" value={fmtDol(perMonth)} color="#9a25ae" />}
                      {goal.targetDate && (
                        <Stat
                          label="Target date"
                          value={new Date(goal.targetDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          color="#555"
                        />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Add goal form */}
        <div style={{ background: t.card, borderRadius: '12px', boxShadow: t.shadow, overflow: 'hidden' }}>
          <div style={{ background: t.tableSubHeader, padding: '10px 16px', fontSize: '12px', fontWeight: '700', color: t.textMid, letterSpacing: '0.04em' }}>
            ADD NEW GOAL
          </div>
          <div style={{ padding: formPadding, display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <FormField label="Goal Name">
              <input type="text" value={draft.name} onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Emergency Fund" style={inputStyle} onKeyDown={(e) => e.key === 'Enter' && addGoal()} />
            </FormField>
            <FormField label="Target Amount">
              <input type="number" value={draft.targetAmount} onChange={(e) => setDraft((p) => ({ ...p, targetAmount: e.target.value }))}
                placeholder="$0.00" style={{ ...inputStyle, width: '110px' }} min="0" step="0.01" />
            </FormField>
            <FormField label="Currently Saved">
              <input type="number" value={draft.currentAmount} onChange={(e) => setDraft((p) => ({ ...p, currentAmount: e.target.value }))}
                placeholder="$0.00" style={{ ...inputStyle, width: '110px' }} min="0" step="0.01" />
            </FormField>
            <FormField label="Target Date (optional)">
              <input type="date" value={draft.targetDate} onChange={(e) => setDraft((p) => ({ ...p, targetDate: e.target.value }))}
                style={inputStyle} />
            </FormField>
            <FormField label="Color">
              <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', maxWidth: '170px' }}>
                {COLORS.map((c) => (
                  <button key={c} onClick={() => setDraft((p) => ({ ...p, color: c }))} style={{
                    width: '22px', height: '22px', borderRadius: '50%', background: c,
                    border: draft.color === c ? `3px solid ${t.text}` : '2px solid transparent',
                    cursor: 'pointer', padding: 0,
                  }} />
                ))}
              </div>
            </FormField>
            <button onClick={addGoal} style={{
              background: '#8dc88e', color: 'white', border: 'none', borderRadius: '24px',
              padding: '10px 28px', cursor: 'pointer', fontSize: '14px', fontWeight: '500',
              fontFamily: 'inherit', alignSelf: 'flex-end',
            }}>
              Add Goal +
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

function SummaryCard({ label, value, accent }) {
  const { t } = useTheme();
  return (
    <div style={{ flex: '1 1 140px', background: t.card, borderLeft: `4px solid ${accent}`, borderRadius: '12px', padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
      <div style={{ fontSize: '10px', color: t.textSecondary, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>{label}</div>
      <div style={{ fontSize: '20px', fontWeight: '700', color: accent }}>{value}</div>
    </div>
  );
}

function Stat({ label, value, color }) {
  const { t } = useTheme();
  return (
    <div>
      <div style={{ fontSize: '10px', color: t.textMuted, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '2px' }}>{label}</div>
      <div style={{ fontSize: '13px', fontWeight: '600', color }}>{value}</div>
    </div>
  );
}

function Badge({ text, bg, color }) {
  return (
    <span style={{ fontSize: '11px', fontWeight: '700', padding: '2px 8px', background: bg, color, borderRadius: '20px', whiteSpace: 'nowrap' }}>
      {text}
    </span>
  );
}

function FormField({ label, children }) {
  const { t } = useTheme();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <label style={{ fontSize: '11px', fontWeight: '600', color: t.textTertiary, letterSpacing: '0.03em' }}>{label}</label>
      {children}
    </div>
  );
}
